import { defineComponent as _defineComponent } from 'vue'
import { type Ref, computed, onMounted, ref, useAsync, useRoute, useStore } from "@nuxtjs/composition-api";
import { type CCol } from "@rscollabs/capsule-wardrobe-components";

import type LtkPostLike from "@/components/Ltk/LtkPostLike.vue";
import { useAmplitude, useBranchService, useTranslate, useVuetify } from "@/composables";
import { useFlag } from "@/composables/experiments";
import { getLtksStore } from "@/store/ltks";
import { getProfilesStore } from "@/store/profiles";
import { time } from "@/utils/enums/time";
import { EXPERIMENTS_POSTS_FAVORITE_VPM, FLAG_POST_WO_PRODUCTS } from "@/utils/experiments/constants";
import { COACHMARK_POST_PAGE } from "@/utils/localstorage/constants";
import { TOOLTIP_REFERRER_POST_PAGE_LIKE_TIMESTAMP } from "@/utils/sessionstorage/constants";


export default /*#__PURE__*/_defineComponent({
  __name: 'LtkItem',
  props: {
  ltkId: {
    type: String,
    required: true,
  },
},
  emits: ["product-position"],
  setup(__props, { emit }: { emit: ({
  (event: "product-position", value: number): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;




const products: Ref<(typeof CCol & HTMLDivElement) | null> = ref(null);
const coachmarkTargetRef: Ref<typeof LtkPostLike | null> = ref(null);
const showCoachmark = ref(false);

const journeyGuidancePostTestName = EXPERIMENTS_POSTS_FAVORITE_VPM;

const store = useStore();
const { t } = useTranslate();
const amplitude = useAmplitude();
const route = useRoute();
const vuetify = useVuetify();
const branch = useBranchService();
const ltksStore = getLtksStore(store);

const ltk = computed(() => {
  return (
    ltksStore.ltks?.[props.ltkId] || {
      videoMediaId: null,
      profileId: null,
      productIds: [],
      matchingProducts: { exact: [], similar: [] },
      shareUrl: null,
    }
  );
});

const isVideo = computed(() => ltk.value?.videoMediaId !== null);
const displayName = computed(() => {
  const profile = getProfilesStore(store).getProfileById(ltk.value?.profileId);
  return profile?.displayName ?? "";
});

const shareTitle = t("{displayName}'s Post on LTK", { displayName: displayName.value });
const shareText = t("Shop the products featured in this Post from {displayName}", { displayName: displayName.value });

const coachmarkText = t("Tap the heart to save your picks for later and be notified before they sell out.");
const noProductsText = t("No tagged products");

const isLikeButtonVisible = computed(() => {
  const MIN_VISIBLE_TOP_POSITION = 80;
  const likeButton = coachmarkTargetRef.value;
  const { top, left, bottom, right } = likeButton?.$el.getBoundingClientRect() || {};

  return (
    top >= MIN_VISIBLE_TOP_POSITION &&
    left >= 0 &&
    bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    right <= (window.innerWidth || document.documentElement.clientWidth)
  );
});

const referringComponent = computed(() => amplitude?.getReferringComponentByPage(route.value.name ?? "") ?? "");

const hasAlreadySeenCoachmark = () => {
  return localStorage.getItem(COACHMARK_POST_PAGE) === "true";
};

const isEligibleForCoachmark = computed(() => {
  return (
    vuetify.breakpoint.xsOnly && !hasAlreadySeenCoachmark() && !branch.isJourneyDisplayed() && isLikeButtonVisible.value
  );
});

const showPostWoProducts = useFlag(FLAG_POST_WO_PRODUCTS);
const hasProducts = computed(() => ltk.value.productIds.length > 0);
const setCoachmarkDisplayedValue = () => {
  try {
    localStorage.setItem(COACHMARK_POST_PAGE, "true");
    // Set a tooltip referrer in session storage with a timestamp
    sessionStorage.setItem(TOOLTIP_REFERRER_POST_PAGE_LIKE_TIMESTAMP, `${Date.now()}`);
  } catch (e) {
    console.error("There is an issue accessing local and/or session storage: ", e);
  }

  localStorage.setItem(COACHMARK_POST_PAGE, "true");
  amplitude?.sendEvent("/web-vpm-displayed", {
    "vpm-type": "favorite a post",
  });
};

/**
 * Position can change after initial render when a screen is resized
 */
const onResize = () => {
  const offsetTop = products.value?.offsetTop ?? 0 + ((products.value?.offsetParent as HTMLElement)?.offsetTop ?? 0);
  emit("product-position", offsetTop);
};

onMounted(() => {
  onResize();

  setTimeout(() => {
    if (isEligibleForCoachmark.value) {
      const likeButton = coachmarkTargetRef.value;
      likeButton?.$el.setAttribute(
        "style",
        `z-index: ${window.getComputedStyle(document.body).getPropertyValue("--z-like-and-follow-button")}`,
      );
      showCoachmark.value = true;
    }
  }, time.THREE_SECONDS);
});

const fetch = async () => {
  const ltksStore = getLtksStore(store);
  // Store/API will aggregate individual calls into a more performant aggregate api request.
  await ltksStore.fetchLtkByIdAggregate({ id: props.ltkId });
};

useAsync(() => fetch());

return { __sfc: true,props, emit, products, coachmarkTargetRef, showCoachmark, journeyGuidancePostTestName, store, t, amplitude, route, vuetify, branch, ltksStore, ltk, isVideo, displayName, shareTitle, shareText, coachmarkText, noProductsText, isLikeButtonVisible, referringComponent, hasAlreadySeenCoachmark, isEligibleForCoachmark, showPostWoProducts, hasProducts, setCoachmarkDisplayedValue, onResize, fetch, time }
}

})